import React, { useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown, MessageSquarePlus, CheckCircle2, X } from "lucide-react";
import { publicApi } from "../lib/api";
import { useI18n } from "../lib/i18n";

/**
 * Post-response feedback widget: thumbs up/down + optional "share your case" textarea.
 * Builds an anonymized corpus of citizen experiences to improve the platform.
 */
export default function FeedbackWidget({ queryLogId }) {
  const { t, locale } = useI18n();
  const storageKey = `lexhab_fb_${queryLogId}`;
  const [rating, setRating] = useState(null); // 'up' | 'down' | null
  const [caseOpen, setCaseOpen] = useState(false);
  const [caseText, setCaseText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [caseSubmitted, setCaseSubmitted] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        setRating(parsed.rating || null);
        setSubmitted(!!parsed.rating);
        setCaseSubmitted(!!parsed.case_submitted);
      }
    } catch (e) {}
  }, [storageKey]);

  const persist = (data) => {
    try {
      const prev = JSON.parse(localStorage.getItem(storageKey) || "{}");
      localStorage.setItem(storageKey, JSON.stringify({ ...prev, ...data }));
    } catch (e) {}
  };

  const vote = async (v) => {
    if (submitted && rating === v) return;
    setRating(v);
    setSubmitted(true);
    persist({ rating: v });
    try {
      await publicApi.feedback({
        query_log_id: queryLogId,
        rating: v,
        language: locale,
      });
    } catch (e) {
      // swallow — we keep UI state optimistic
    }
  };

  const submitCase = async () => {
    const txt = caseText.trim();
    if (!txt) return;
    try {
      await publicApi.feedback({
        query_log_id: queryLogId,
        rating: rating || "up",
        case_text: txt,
        language: locale,
      });
      setCaseSubmitted(true);
      persist({ case_submitted: true });
      setCaseOpen(false);
    } catch (e) {
      // keep open on error
    }
  };

  return (
    <div
      className="mt-5 pt-4 border-t border-primary/10 flex flex-col gap-3"
      data-testid={`feedback-widget-${queryLogId}`}
    >
      <div className="flex items-center flex-wrap gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/50">
          {locale === "fr" ? "Cette réponse vous a-t-elle aidé ?" : "Did this answer help?"}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => vote("up")}
            disabled={submitted && rating === "up"}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border text-xs font-medium transition-all ${
              rating === "up"
                ? "bg-[#D1FAE5] border-[#A7F3D0] text-[#065F46]"
                : "border-primary/20 text-foreground/60 hover:border-primary hover:text-primary hover:bg-primary/5"
            }`}
            data-testid={`feedback-up-${queryLogId}`}
          >
            <ThumbsUp size={13} />
            {locale === "fr" ? "Utile" : "Helpful"}
          </button>
          <button
            type="button"
            onClick={() => vote("down")}
            disabled={submitted && rating === "down"}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border text-xs font-medium transition-all ${
              rating === "down"
                ? "bg-[#FEE2E2] border-[#FECACA] text-[#991B1B]"
                : "border-primary/20 text-foreground/60 hover:border-[#E76F51] hover:text-[#E76F51] hover:bg-[#E76F51]/5"
            }`}
            data-testid={`feedback-down-${queryLogId}`}
          >
            <ThumbsDown size={13} />
            {locale === "fr" ? "Peu utile" : "Not helpful"}
          </button>
        </div>

        {!caseSubmitted && submitted && !caseOpen && (
          <button
            type="button"
            onClick={() => setCaseOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-dark hover:text-accent transition-colors ml-auto"
            data-testid={`feedback-open-case-${queryLogId}`}
          >
            <MessageSquarePlus size={13} />
            {locale === "fr" ? "Partager votre cas" : "Share your case"}
          </button>
        )}

        {caseSubmitted && (
          <span
            className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#065F46] ml-auto"
            data-testid={`feedback-case-submitted-${queryLogId}`}
          >
            <CheckCircle2 size={13} />
            {locale === "fr" ? "Cas transmis. Merci." : "Case submitted. Thank you."}
          </span>
        )}
      </div>

      {caseOpen && !caseSubmitted && (
        <div
          className="bg-[#F8F9FA] border border-primary/15 rounded-sm p-3 animate-fade-in"
          data-testid={`feedback-case-box-${queryLogId}`}
        >
          <div className="flex items-start justify-between mb-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary font-semibold">
              {locale === "fr"
                ? "Partagez votre situation (anonyme)"
                : "Share your situation (anonymous)"}
            </label>
            <button
              type="button"
              onClick={() => {
                setCaseOpen(false);
                setCaseText("");
              }}
              className="text-foreground/40 hover:text-foreground/70"
              data-testid={`feedback-case-close-${queryLogId}`}
            >
              <X size={14} />
            </button>
          </div>
          <textarea
            value={caseText}
            onChange={(e) => setCaseText(e.target.value)}
            maxLength={2000}
            rows={4}
            placeholder={
              locale === "fr"
                ? "Ex. « J'ai déposé un dossier en mars, toujours sans réponse à ce jour… »"
                : "E.g. “I filed a request in March, still no answer to date…”"
            }
            className="w-full bg-white border border-primary/20 rounded-sm px-3 py-2 text-sm focus:border-primary outline-none resize-none"
            data-testid={`feedback-case-textarea-${queryLogId}`}
          />
          <div className="flex items-center justify-between mt-2">
            <span className="font-mono text-[10px] text-foreground/40">
              {caseText.length}/2000
            </span>
            <button
              type="button"
              onClick={submitCase}
              disabled={!caseText.trim()}
              className="inline-flex items-center gap-1.5 bg-primary text-white px-3 py-1.5 rounded-sm text-xs font-medium hover:bg-primary-light transition-colors disabled:opacity-50"
              data-testid={`feedback-case-submit-${queryLogId}`}
            >
              {locale === "fr" ? "Transmettre" : "Submit"}
            </button>
          </div>
          <p className="font-mono text-[10px] text-foreground/45 mt-2 leading-relaxed">
            {locale === "fr"
              ? "Votre témoignage est anonymisé et aide la plateforme à améliorer le service aux citoyens."
              : "Your testimony is anonymized and helps the platform improve its service to citizens."}
          </p>
        </div>
      )}
    </div>
  );
}
